import React, { lazy } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';

import PublicLayout from '../Components/layouts/PublicLayout';
import PrivateLayout from '../Components/layouts/PrivateLayout';
import PrivateShell from '../Components/layouts/PrivateShell';
import AccountPickerLayout from '../Components/layouts/AccountPickerLayout';
import { AuthProvider } from '../context/AuthContext';

import LandingPage from '../pages/LandingPage';
import usePageTitle from '../hooks/usePageTitle';
import RequirePermission from '../Components/auth/RequirePermission';
import RequireWidgetAccess from '../pages/productos/SocialMedia/widget/RequireWidgetAccess';
import EnforceRolePermissions from '../Components/auth/EnforceRolePermissions';
import RequireAccount from '../Components/auth/RequireAccount';
import RequirePlatformAdmin from '../Components/auth/RequirePlatformAdmin';
import RequireIncidentsAccess from '../Components/auth/RequireIncidentsAccess';
import RequireProduct from '../Components/products/RequireProduct';
import LegacyRedirect, {
  RedirectWithoutAccountPrefix,
} from '../Components/layouts/LegacyRedirect';

const Login = lazy(() => import('../pages/Login'));
const Confirm = lazy(() => import('../pages/Confirm'));
const ChangePassword = lazy(() => import('../pages/ChangePassword'));
const ChangeTemporaryPassword = lazy(() => import('../pages/ChangeTemporaryPassword'));
const Home = lazy(() => import('../pages/Home'));
const Limits = lazy(() => import('../pages/Limits'));
const ResetPassword = lazy(() => import('../pages/ResetPassword'));
const Queues = lazy(() => import('../pages/Queues'));
const Contactability = lazy(() => import('../pages/Queues/Contactability'));
const HistorySms = lazy(() => import('../pages/productos/sms/History'));
const HistoryVoice = lazy(() => import('../pages/productos/Voz/History'));
const VirtualAgent = lazy(() => import('../pages/productos/virtual-agent'));
const VirtualAgentVoz = lazy(() => import('../pages/productos/virtual-agent/voz'));
const VirtualAgentChat = lazy(() => import('../pages/productos/virtual-agent/chat'));
const ConsumosChat = lazy(() => import('../pages/productos/virtual-agent/chat/consumos'));
const FlowReportsPage = lazy(() => import('../pages/productos/virtual-agent/chat/reportes'));
const StepTracking = lazy(() => import('../pages/productos/virtual-agent/chat/StepTracking'));const VoiceReports = lazy(() => import('../pages/productos/Voz/Reports'));
const SmsReports = lazy(() => import('../pages/productos/sms/Reports'));
const MarcadorReports = lazy(() => import('../pages/productos/marcador/Reports'));
// Los dos reportes de desempeno de agentes comparten pantalla: el contenedor decide que
// pestana mostrar segun la ruta, y cada una sigue detras de su propio permiso.
const AgentReports = lazy(() =>
  import('../pages/productos/marcador/AgentReports')
);
const Portafolio = lazy(() => import('../pages/productos/portafolio/Index'));
const Integracion = lazy(() => import('../pages/productos/Integraciones'));
const ChatBot = lazy(() => import('../pages/productos/chatBot'));
const Voice = lazy(() => import('../pages/productos/Voz'));
const SMS = lazy(() => import('../pages/productos/sms'));
const Marcador = lazy(() => import('../pages/productos/marcador'));
const Agent = lazy(() => import('../pages/productos/Scheduler'));
const Accounts = lazy(() => import('../pages/Accounts'));
const AccountDetail = lazy(() => import('../pages/Accounts/Detail'));
const Users = lazy(() => import('../pages/Users'));
const ProductsAdmin = lazy(() => import('../pages/ProductsAdmin'));
const Consumption = lazy(() => import('../pages/Consumption'));
const PhoneDetail = lazy(() => import('../pages/MetaTemplates/views/PhoneDetail'));
const CatalogPage = lazy(() => import('../pages/Catalog'));
const PhoneValidator = lazy(() => import('../pages/productos/PhoneValidator'));
const SocialMedia = lazy(() => import('../pages/productos/SocialMedia'));
const WebChannelListPage = lazy(() => import('../pages/productos/SocialMedia/widget/WebChannelListPage'));
const WebWidgetHub = lazy(() => import('../pages/productos/SocialMedia/widget/WebWidgetHub'));
const WebChannelConfigPage = lazy(() => import('../pages/productos/SocialMedia/widget/WebChannelConfigPage'));
const WebWidgetLimitsPage = lazy(() => import('../pages/productos/SocialMedia/widget/WebWidgetLimitsPage'));
const WebchatReportPage = lazy(() => import('../pages/productos/SocialMedia/widget/WebchatReportPage'));
const SocialMediaInbox = lazy(() => import('../pages/productos/SocialMedia/InboxSection'));
const PropertiesValidator = lazy(() => import('../pages/PropertiesValidator'));
const RecepcionistaVirtual = lazy(() => import('../pages/productos/recepcionistaVirtual/Index'));
const MetaTemplates = lazy(() => import('../pages/MetaTemplates'));
const EditMetaTemplate = lazy(() => import('../pages/MetaTemplates/views/EditTemplate'));
const CreateMetaTemplate = lazy(() => import('../pages/MetaTemplates/views/CreateTemplate'));
const VirtualAuditors = lazy(() => import('../pages/VirtualAuditors'));
const VirtualAuditorsHome = lazy(() =>
  import('../pages/VirtualAuditors/sections/HomePage')
);
const VirtualAuditorsList = lazy(() =>
  import('../pages/VirtualAuditors/sections/AuditorsListPage')
);
const VirtualAuditorEditor = lazy(() =>
  import('../pages/VirtualAuditors/sections/AuditorEditorPage')
);
const VirtualAudits = lazy(() =>
  import('../pages/VirtualAuditors/sections/AuditsPage')
);
const VirtualAuditorsUsage = lazy(() =>
  import('../pages/VirtualAuditors/sections/UsagePage')
);
const VirtualAuditorsKnowledgeBase = lazy(() =>
  import('../pages/VirtualAuditors/sections/KnowledgeBasePage')
);
const PlatformProfiles = lazy(() => import('../pages/PlatformProfiles'));
const Audit = lazy(() => import('../pages/Audit'));
const Incidents = lazy(() => import('../pages/Incidents'));

function PageTitleSync() {
  usePageTitle();
  return null;
}

function ProductRoute({ children }) {
  return (
    <RequireProduct>
      <EnforceRolePermissions>{children}</EnforceRolePermissions>
    </RequireProduct>
  );
}

export default function AppRouter() {
  return (
    <Router>
      <AuthProvider>
        <PageTitleSync />
        <Routes>
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/auth" element={<PublicLayout />}>
            <Route path="login" element={<Login />} />
            <Route path="resetpassword" element={<ResetPassword />} />
            <Route
              path="confirmnewpassword/:email"
              element={<ChangePassword />}
            />
            <Route
              path="change-temporary-password"
              element={<ChangeTemporaryPassword />}
            />
            <Route path="confirm/:email" element={<Confirm />} />
            <Route path="*" element={<Navigate to="/auth/login" />} />
          </Route>
          <Route path="/" element={<PrivateShell />}>
            <Route index element={<Navigate to="/accounts" replace />} />
            <Route element={<AccountPickerLayout />}>
              <Route path="accounts" element={<Accounts />} />
              <Route
                path="accounts/:clientId"
                element={
                  <RequirePlatformAdmin>
                    <AccountDetail />
                  </RequirePlatformAdmin>
                }
              />
              <Route
                path="platform/profiles"
                element={
                  <RequirePlatformAdmin>
                    <PlatformProfiles />
                  </RequirePlatformAdmin>
                }
              />
              <Route
                path="platform/audit"
                element={
                  <RequirePlatformAdmin>
                    <Audit />
                  </RequirePlatformAdmin>
                }
              />
              <Route
                path="platform/incidents"
                element={
                  <RequireIncidentsAccess>
                    <Incidents />
                  </RequireIncidentsAccess>
                }
              />
              <Route
                path="consumption"
                element={
                  <RequirePlatformAdmin>
                    <Consumption />
                  </RequirePlatformAdmin>
                }
              />
              <Route
                path="products/*"
                element={
                  <RequirePlatformAdmin>
                    <ProductsAdmin />
                  </RequirePlatformAdmin>
                }
              />
            </Route>
            <Route path=":accountId" element={<RequireAccount />}>
              <Route element={<PrivateLayout />}>
                <Route index element={<Navigate to="home" replace />} />
                <Route path="home" element={<Home />} />
                <Route
                  path="limits"
                  element={
                    <RequirePermission section="limits">
                      <Limits />
                    </RequirePermission>
                  }
                />
                <Route
                  path="users"
                  element={
                    <RequirePermission section="platform.users">
                      <Users />
                    </RequirePermission>
                  }
                />
                <Route
                  path="products/*"
                  element={<RedirectWithoutAccountPrefix />}
                />
                <Route
                  path="consumption"
                  element={<Navigate to="/consumption" replace />}
                />

                <Route
                  path="service/sms/reportes"
                  element={
                    <ProductRoute>
                      <SmsReports />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/call-blasting/consumos"
                  element={
                    <ProductRoute>
                      <HistoryVoice />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/virtual-agent"
                  element={
                    <ProductRoute>
                      <VirtualAgent />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/virtual-auditors"
                  element={
                    <ProductRoute>
                      <VirtualAuditors />
                    </ProductRoute>
                  }
                >
                  {/* Rutas relativas: la seccion vive bajo /:accountId/service/... */}
                  <Route
                    index
                    element={<VirtualAuditorsHome />}
                  />
                  <Route
                    path="auditores"
                    element={<VirtualAuditorsList />}
                  />
                  <Route
                    path="auditores/:auditorId"
                    element={<VirtualAuditorEditor />}
                  />
                  <Route path="auditorias" element={<VirtualAudits />} />
                  <Route path="consumo" element={<VirtualAuditorsUsage />} />
                  <Route
                    path="base-de-conocimiento"
                    element={<VirtualAuditorsKnowledgeBase />}
                  />
                  <Route
                    path="base-de-conocimiento/:knowledgeBaseId"
                    element={<VirtualAuditorsKnowledgeBase />}
                  />
                  {/* KnowledgeBasesTab agrega /knowledge-bases al basePath al navegar. */}
                  <Route
                    path="base-de-conocimiento/knowledge-bases"
                    element={<VirtualAuditorsKnowledgeBase />}
                  />
                  <Route
                    path="base-de-conocimiento/knowledge-bases/:knowledgeBaseId"
                    element={<VirtualAuditorsKnowledgeBase />}
                  />
                  <Route
                    path="*"
                    element={<Navigate to=".." replace />}
                  />
                </Route>
                <Route
                  path="service/virtual-agent/voz/*"
                  element={
                    <ProductRoute>
                      <VirtualAgentVoz />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/virtual-agent/chat/consumos"
                  element={
                    <ProductRoute>
                      <ConsumosChat />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/virtual-agent/chat/reportes"
                  element={
                    <ProductRoute>
                      <FlowReportsPage />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/virtual-agent/chat/seguimiento-pasos"
                  element={
                    <ProductRoute>
                      <StepTracking />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/virtual-agent/chat/*"
                  element={
                    <ProductRoute>
                      <VirtualAgentChat />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/call-blasting/reportes"
                  element={
                    <ProductRoute>
                      <VoiceReports />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/sms/consumos"
                  element={
                    <ProductRoute>
                      <HistorySms />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/agent-manager/reportes"
                  element={
                    <ProductRoute>
                      <MarcadorReports />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/agent-manager/ocupacion"
                  element={
                    <ProductRoute>
                      <RequirePermission section="agent-manager.ocupacion">
                        <AgentReports />
                      </RequirePermission>
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/agent-manager/llamadas-extension"
                  element={
                    <ProductRoute>
                      <RequirePermission section="agent-manager.llamadas-extension">
                        <AgentReports />
                      </RequirePermission>
                    </ProductRoute>
                  }
                />

                <Route
                  path="service/portafolio"
                  element={
                    <ProductRoute>
                      <Portafolio />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/integraciones"
                  element={
                    <ProductRoute>
                      <Integracion />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/chat-bot"
                  element={
                    <ProductRoute>
                      <ChatBot />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/agent-manager/colas"
                  element={
                    <ProductRoute>
                      <Queues />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/agent-manager/contactabilidad"
                  element={
                    <ProductRoute>
                      <Contactability />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/call-blasting"
                  element={
                    <ProductRoute>
                      <Voice />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/agendador"
                  element={
                    <ProductRoute>
                      <Agent />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/agent-manager"
                  element={
                    <ProductRoute>
                      <Marcador />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/sms"
                  element={
                    <ProductRoute>
                      <SMS />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/recepcionista"
                  element={
                    <ProductRoute>
                      <RecepcionistaVirtual />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/phone-validator"
                  element={
                    <ProductRoute>
                      <PhoneValidator />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/social-media"
                  element={
                    <ProductRoute>
                      <SocialMedia />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/social-media/widget"
                  element={
                    <ProductRoute>
                      <RequireWidgetAccess>
                        <WebWidgetHub />
                      </RequireWidgetAccess>
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/social-media/widget/list"
                  element={
                    <ProductRoute>
                      <RequireWidgetAccess>
                        <WebChannelListPage />
                      </RequireWidgetAccess>
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/social-media/widget/report"
                  element={
                    <ProductRoute>
                      <WebchatReportPage />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/social-media/widget/limits"
                  element={
                    <ProductRoute>
                      <RequireWidgetAccess>
                        <WebWidgetLimitsPage />
                      </RequireWidgetAccess>
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/social-media/widget/new"
                  element={
                    <ProductRoute>
                      <RequireWidgetAccess>
                        <WebChannelConfigPage />
                      </RequireWidgetAccess>
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/social-media/widget/:channelId"
                  element={
                    <ProductRoute>
                      <RequireWidgetAccess>
                        <WebChannelConfigPage />
                      </RequireWidgetAccess>
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/social-media/*"
                  element={
                    <ProductRoute>
                      <SocialMediaInbox />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/phone-validator/properties"
                  element={
                    <ProductRoute>
                      <PropertiesValidator />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/nthw-templates"
                  element={
                    <ProductRoute>
                      <MetaTemplates />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/whatsapp/catalog"
                  element={
                    <ProductRoute>
                      <CatalogPage />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/whatsapp/limits"
                  element={
                    <ProductRoute>
                      <MetaTemplates />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/whatsapp/phones/:systemNumber/template/:templateId"
                  element={
                    <ProductRoute>
                      <PhoneDetail />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/whatsapp/phones/:systemNumber"
                  element={
                    <ProductRoute>
                      <PhoneDetail />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/whatsapp/*"
                  element={
                    <ProductRoute>
                      <MetaTemplates />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/nthw-templates/create"
                  element={
                    <ProductRoute>
                      <CreateMetaTemplate />
                    </ProductRoute>
                  }
                />
                <Route
                  path="service/nthw-templates/:templateId/edit"
                  element={
                    <ProductRoute>
                      <EditMetaTemplate />
                    </ProductRoute>
                  }
                />
                <Route path="*" element={<Navigate to="home" replace />} />
              </Route>
            </Route>
            <Route path="*" element={<LegacyRedirect />} />
          </Route>
        </Routes>
      </AuthProvider>
    </Router>
  );
}
